<template>
  <section>
    <div class="d-flex flex-column align-items-center border rounded p-4">
      <p class="mb-0 w-100">
        There {{ sectionsTotalCount === 1 ? (rollover.executedAt ? 'was' : 'is') : (rollover.executedAt ? 'were' : 'are') }}
        <strong>{{ sectionsTotalCount }}</strong> {{ sectionsTotalCount > 1 ? 'Sections' : 'Section' }} and
        <strong>{{ coursesTotalCount }}</strong> {{ coursesTotalCount > 1 ? 'Courses' : 'Course' }} matching search and filter criteria.
        <br>
        We {{ rollover.executedAt ? 'rolled over' : 'will rollover' }} only these, excluding any selected exceptions.
      </p>
      <hr class="w-100" />
      <p class="mb-0 w-100">
        We {{ rollover.executedAt ? 'rolled over' : 'will rollover' }}
        from <strong>{{ fromTermDisplayName }}</strong> to <strong>{{ toTermDisplayName }}</strong>.
      </p>
      <hr class="w-100" />
      <div class="row w-100">
        <div class="col-6 pl-0">
          <p class="mb-0 w-100">
            <span>We {{ rollover.executedAt ? 'did not' : 'will not' }} rollover these Section Fields by default:</span>
            <ul class="list-unstyled mt-2 mb-0">
              <li
                v-for="question of neverRolledQuestions"
                :key="question.questionId"
              >
                <strong>- {{ question.label }}</strong> <span class="ml-2 text-muted">({{ question.questionId }})</span>
              </li>
            </ul>
          </p>
          <p class="mb-0 mt-3 w-100">
            <span>We {{ rollover.executedAt ? 'had to' : 'must' }} rollover these Section Fields by default:</span>
            <ul class="list-unstyled mt-2 mb-0">
              <li
                v-for="question of alwaysRolledQuestions"
                :key="question.questionId"
              >
                <strong>- {{ question.label }}</strong> <span class="ml-2 text-muted">({{ question.questionId }})</span>
              </li>
            </ul>
          </p>
        </div>
        <div class="col-6 pr-0">
          <p v-if="excludedRolloverQuestions.length" class="mb-0 w-100">
            <span>We {{ rollover.executedAt ? 'did not' : 'will not' }} rollover these selected Section Fields:</span>
            <ul class="list-unstyled mt-2 mb-0">
              <li
                v-for="question of excludedRolloverQuestions"
                :key="question.questionId"
              >
                <strong>- {{ question.label }}</strong> <span class="ml-2 text-muted">({{ question.questionId }})</span>
              </li>
            </ul>
          </p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { ref, toRefs, computed, watch } from '@vue/composition-api'
import { postCoursesAdvancedSearch, postSectionsSearch } from '@/api'
import SectionRollover from '@shared/sm/rollovers/SectionRollover'
import useSectionTemplate from '@sm/client/composables/useSectionTemplate'
import { prepareFilterForApi } from '@/components/composables/useFilter'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { SM_ROLLOVERS_NESTED_AND_OR_FILTER } from '@shared/constants/featureFlags'
import filterUtils from '@shared/utils/filters'

export default {
  name: 'RolloverSectionsSummary',
  props: {
    rollover: {
      type: Object,
      default: () => ({}),
    },
  },
  setup (props, { root }) {
    const { isRolloverNestedAndOrFilterActive } = useFeatureFlags({
      isRolloverNestedAndOrFilterActive: SM_ROLLOVERS_NESTED_AND_OR_FILTER,
    })
    const { rollover } = toRefs(props)

    const isNestedFilterActive = filterUtils.isNestedFilterActive(
      isRolloverNestedAndOrFilterActive,
      !!rollover.value.query?.filter,
      !!rollover.value.query?.nestedFilter
    )

    const { search, filter, nestedFilter } = rollover.value.query

    const sectionsTotalCount = ref(0)
    const coursesTotalCount = ref(0)
    const isFetching = ref(false)

    const sectionsCount = computed(() => sectionsTotalCount.value - rollover.value.excludedSections.length)
    const coursesCount = computed(() => coursesTotalCount.value - rollover.value.excludedCourses.length)

    const { sectionTemplate } = useSectionTemplate()

    const sectionRollover = computed(() => new SectionRollover(rollover.value))

    const neverRolledQuestions = computed(() =>
      SectionRollover.neverRolledQuestions(sectionTemplate.value)
    )
    const alwaysRolledQuestions = computed(() =>
      SectionRollover.alwaysRolledQuestions(sectionTemplate.value)
    )
    const excludedRolloverQuestions = computed(() =>
      sectionRollover.value.excludedRolloverQuestions(sectionTemplate.value)
    )

    // TODO: Use useSections composable
    async function fetchSectionsAndCoursesTotals () {
      try {
        isFetching.value = true
        const commonParams = {
          school: root.$store.getters.school,
          year: rollover.value.fromYear,
          semester: rollover.value.fromSemester,
          // We only care about the total counts here
          limit: 1,
          skip: 0,
        }
        // It is possible to search for sections using POST /courses-search?skipBy=sections,
        // but the search machanism is bugged there. Once it's fixed we can just one:
        // TODO: https://www.notion.so/coursedog1/Sort-out-getting-courses-sections-endpoints-ae4989e79ddc419c9e84c4e547266e7d
        const [ sectionsResponse, coursesResponse ] = await Promise.all([
          postSectionsSearch(
            {
              ...commonParams,
              skipBy: 'sections',
              // TODO: Once this endpoint is refactored to not have
              // `searchQuery` in param, we can get rid of this.
              searchQuery: search || '$filters',
            },
            {
              ...(isNestedFilterActive
                ? {
                  nestedFilter: { ...prepareFilterForApi(nestedFilter) },
                }
                : prepareFilterForApi(filter)),
            }
          ),
          postCoursesAdvancedSearch(
            {
              ...commonParams,
              ...(search && { searchQuery: search }),
            },
            {
              ...(isNestedFilterActive
                ? {
                  nestedFilter: { ...prepareFilterForApi(nestedFilter) },
                }
                : prepareFilterForApi(filter)),
            }
          ),
        ])
        // TODO: These could be omited once we fix errors interceptor
        if (sectionsResponse instanceof Error) throw sectionsResponse
        if (sectionsResponse.error instanceof Error) throw sectionsResponse.error
        if (coursesResponse instanceof Error) throw coursesResponse
        if (coursesResponse.error instanceof Error) throw coursesResponse.error

        sectionsTotalCount.value = sectionsResponse.numberOfSections
        coursesTotalCount.value = coursesResponse.numberOfCourses
      } catch (err) {
        console.error(err)
      } finally {
        isFetching.value = false
      }
    }

    watch(
      [ rollover ],
      () => {
        fetchSectionsAndCoursesTotals()
      },
      { immediate: true }
    )

    return {
      sectionsTotalCount,
      coursesTotalCount,
      sectionsCount,
      coursesCount,
      neverRolledQuestions,
      alwaysRolledQuestions,
      excludedRolloverQuestions,
      isRolloverNestedAndOrFilterActive: isNestedFilterActive,
    }
  },
  computed: {
    ...mapGetters([ 'school', 'termDisplayName', 'termByYearAndSemester' ]),
    fromTermDisplayName () {
      return this.termDisplayName(this.termByYearAndSemester({ year: this.rollover.fromYear, semester: this.rollover.fromSemester }))
    },
    toTermDisplayName () {
      return this.termDisplayName(this.termByYearAndSemester({ year: this.rollover.toYear, semester: this.rollover.toSemester }))
    },
  },
  created () {
    this.$store.dispatch('loadTerms', { school: this.school })
  },
}
</script>
